import { useEffect, useRef, useState } from 'react'
import type { CrystalOrder, MonetizationView } from '@mezha/api'
import { vkCommerce, type RewardOutcome } from '../../platform/vkCommerce'
import { platform } from '../../platform/detect'
import { PlankButton, ConfirmInPlace } from '../../ui'
import { clockText } from '../../lib/time'
import { useMeQuery } from '../../api/endpoints/auth'

const statusText: Record<string,string> = { pending:'Ожидаем подтверждение VK', granted:'Кристаллы начислены', test_completed:'Тест принят — без начисления', refunded:'Возврат обработан', test_refunded:'Тестовый возврат обработан' }

/** Advertising starts only from this explicit button while the player is in a city. */
function pauseMedia() {
  const playing = [...document.querySelectorAll<HTMLMediaElement>('audio,video')].filter(e => !e.paused)
  playing.forEach(e => e.pause())
  return () => { playing.forEach(e => { void e.play().catch(() => {}) }) }
}

export function CrystalFunding({ refresh }: { refresh: () => void }) {
  const me = useMeQuery().data
  const key = me ? `mezha.funding.pending.${me.account_id}` : null
  const [view,setView] = useState<MonetizationView | null>(null)
  const [orders,setOrders] = useState<CrystalOrder[]>([])
  const [busy,setBusy] = useState(false)
  const [message,setMessage] = useState('')
  const [error,setError] = useState('')
  const guard = useRef(false)
  const purchase = useRef<{sku:string;requestID:string} | null>(null)
  const pendingAd = useRef<Extract<RewardOutcome,{status:'pending'}> | null>(null)
  const [retryAd,setRetryAd] = useState(false)
  const [retryPurchase,setRetryPurchase] = useState(false)
  const persist = () => { try { if (key) sessionStorage.setItem(key,JSON.stringify({purchase:purchase.current,ad:pendingAd.current})) } catch { /* optional webview storage */ } }
  useEffect(() => {
    if (!key) return
    try { const raw=sessionStorage.getItem(key); if(raw) { const saved=JSON.parse(raw) as {purchase:typeof purchase.current;ad:typeof pendingAd.current}; purchase.current=saved.purchase;pendingAd.current=saved.ad;setRetryPurchase(!!saved.purchase);setRetryAd(!!saved.ad) } } catch { /* no recoverable local record */ }
  },[key])
  const reload = async () => {
    const flow = await vkCommerce()
    const [v,o] = await Promise.all([flow.view(),flow.recoverOrders()])
    setView(v); setOrders(o.orders); refresh()
  }
  useEffect(() => { if (platform() === 'vk') void reload().catch(() => setError('Не удалось проверить доступность. Попробуйте обновить.')) }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const execute = async (fn: () => Promise<void>) => {
    if (guard.current) return
    guard.current=true;setBusy(true);setError('');setMessage('')
    try { await fn() } catch { setError('Связь прервалась. Проверьте результат операции перед новой попыткой.') }
    finally { guard.current=false;setBusy(false) }
  }
  const buy = (sku:string) => execute(async () => {
    if (!purchase.current) purchase.current={sku,requestID:crypto.randomUUID()}
    persist()
    setRetryPurchase(true)
    const result=await (await vkCommerce()).purchase(purchase.current.sku,purchase.current.requestID,false,retryPurchase)
    setMessage(result.dialog === 'cancelled' ? 'Покупка отменена.' : result.dialog === 'failed' ? 'VK сообщил об ошибке оплаты.' : statusText[result.order.status] ?? 'Проверяем результат оплаты.')
    // A server order exists: reconciliation below is read-only even after reload.
    purchase.current=null;setRetryPurchase(false)
    persist()
    await reload()
  })
  const watch = () => execute(async () => {
    const flow=await vkCommerce()
    const result=pendingAd.current ? await flow.completeReward(pendingAd.current.session,pendingAd.current.requestId) : await flow.watchReward(crypto.randomUUID(),pauseMedia)
    pendingAd.current=result.status === 'pending' ? result : null;setRetryAd(result.status === 'pending')
    persist()
    setMessage(result.status === 'completed' ? 'Награда подтверждена сервером.' : result.status === 'pending' ? 'Просмотр завершён. Осталось получить подтверждение награды.' : result.status === 'closed' ? 'Просмотр не завершён, награда не начислена.' : 'Сейчас нет доступной рекламы. Попробуйте позже.')
    await reload()
  })
  if (platform() !== 'vk') return <section className="parch crystal-shop__exchange"><h2>Получить кристаллы</h2><p>Покупки и реклама доступны внутри приложения VK. Здесь можно потратить уже имеющиеся кристаллы.</p><a href="https://vk.com/app54834078" target="_blank" rel="noreferrer">Открыть «Межу» в VK</a></section>
  return <div className="crystal-shop__funding" aria-busy={busy}>
    {error && <p role="alert">{error}</p>}{message && <p role="status">{message}</p>}
    <section className="parch"><h2>Пополнить кристаллы</h2>
      {!view && <p>Проверяем доступность…</p>}
      {view && !view.payments && <p>Покупки сейчас недоступны. Уже имеющиеся кристаллы можно использовать у торговца.</p>}
      {view?.test_mode && <p>Тестовый режим VK: покупки не начисляют кристаллы.</p>}
      {view?.payments && <div className="actions">{view.products.map(p => <ConfirmInPlace key={p.sku} question={<>Получить {p.crystals} кристаллов за {p.price_votes} голосов VK{view.test_mode ? ' в тестовом режиме без начисления' : ''}?</>} yes="Перейти к оплате" onConfirm={() => buy(p.sku)}>{ask => <PlankButton gold disabled={busy || retryPurchase} onClick={ask}>{p.title} · {p.price_votes} голосов</PlankButton>}</ConfirmInPlace>)}</div>}
      {retryPurchase && <PlankButton disabled={busy} onClick={() => purchase.current && void buy(purchase.current.sku)}>Проверить незавершённую покупку</PlankButton>}
      {orders.some(o => o.status === 'pending') && <p>Есть незавершённый заказ. Обновите статус — повторное окно оплаты не откроется.</p>}
    </section>
    <section className="parch"><h2>Кристаллы за просмотр</h2><p>{view?.rewarded ? `Полный просмотр: ${view.ad_reward} кристаллов. Осталось ${view.ad_remaining} на сегодня. Обновление в ${clockText(view.reset_at)}.` : 'Реклама сейчас недоступна.'}</p><PlankButton disabled={busy || (!retryAd && (!view?.rewarded || view.ad_remaining < 1))} onClick={() => void watch()}>{retryAd ? 'Получить подтверждение награды' : 'Смотреть рекламу'}</PlankButton></section>
    <section className="parch"><h2>Мои покупки</h2><PlankButton disabled={busy} onClick={() => void execute(reload)}>Обновить статусы и баланс</PlankButton>{orders.map(o => <p key={o.id}>{o.crystals} кристаллов · {statusText[o.status] ?? o.status}{o.debt > 0 ? ` · долг ${o.debt}` : ''}</p>)}</section>
  </div>
}
